import { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/apiBase';

const ACTIVE = ['requested', 'acknowledged', 'arranged', 'enrolled'];

// One simple training decision. The organisation owns booking and payment.
// Requests survive reload and the server de-duplicates repeat submissions.
export default function WorkerTrainingRequestButton({ trainings = [] }) {
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(true);
  const [requestedCodes, setRequestedCodes] = useState([]);
  const [bookingExists, setBookingExists] = useState(false);
  const [feedback, setFeedback] = useState('');

  const codes = Array.from(new Set(trainings
    .filter(item => ['missing', 'expired', 'due_soon', 'rejected'].includes(item?.status || 'missing'))
    .map(item => String(item?.id || '').trim())
    .filter(code => /^[a-z0-9_]{2,100}$/.test(code))));
  const codesKey = codes.join(',');

  useEffect(() => {
    let current = true;
    const token = localStorage.getItem('workerToken');
    if (!token || !codesKey) {
      setChecking(false);
      return undefined;
    }
    setChecking(true);
    axios.get(`${API_BASE}/worker/training/hub`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => {
      if (!current) return;
      const open = (response.data?.support_requests || [])
        .filter(req => req.kind === 'training' && ACTIVE.includes(req.status));
      setRequestedCodes(open.map(req => req.training_code));
      setBookingExists(open.some(req => req.status === 'enrolled'
        && ['hsg_cstf_one_day_refresher', 'hsg_care_certificate_initial'].includes(req.training_code)));
    }).catch(() => {
      // Temporary status failure must not prevent a legitimate request. The
      // server owns de-duplication and tenant isolation regardless of UI state.
    }).finally(() => { if (current) setChecking(false); });
    return () => { current = false; };
  }, [codesKey]);

  const remaining = codes.filter(code => !requestedCodes.includes(code));
  const requestReceived = !bookingExists && remaining.length === 0;

  const requestTraining = async () => {
    if (sending || checking || bookingExists || requestReceived || remaining.length === 0) return;
    const token = localStorage.getItem('workerToken');
    if (!token) {
      setFeedback('Please sign in again to request training.');
      return;
    }
    setSending(true);
    setFeedback('');
    try {
      const outcome = await Promise.allSettled(remaining.map(code => axios.post(
        `${API_BASE}/worker/training/hub/support-requests`,
        { kind: 'training', training_code: code },
        { headers: { Authorization: `Bearer ${token}` } },
      )));
      const saved = remaining.filter((_code, index) => outcome[index].status === 'fulfilled');
      setRequestedCodes(old => Array.from(new Set([...old, ...saved])));
      setFeedback(saved.length === remaining.length
        ? 'Request received. Your organisation will arrange the training.'
        : saved.length
          ? 'Some training requests were received. Try again to send the rest.'
          : 'We could not send your request. Please try again.');
    } finally { setSending(false); }
  };

  if (codes.length === 0) return null;
  if (bookingExists || requestReceived) {
    return (
      <div className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-3 text-sm text-teal-900"
        role="status" data-testid="worker-training-request-status">
        <p className="font-semibold">{bookingExists ? 'Training arranged' : 'Request received'}</p>
        <p className="mt-1 text-xs">
          {bookingExists
            ? 'Your organisation arranged training. Attend it, then upload the certificate beside the relevant course.'
            : 'Your organisation already has your request. No need to request the same training again.'}
        </p>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col items-stretch gap-1">
      <button type="button" disabled={checking || sending}
        onClick={requestTraining}
        className="min-h-12 w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        data-testid="request-mandatory-training">
        {checking ? 'Checking…' : sending ? 'Sending…' : 'I need training'}
      </button>
      {feedback && <p role="status" className="text-xs text-slate-700">{feedback}</p>}
    </div>
  );
}
